import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useClient } from 'urql'
import {
  readCateringVouchersQuery,
  readOneGroupQuery,
  readOneMembershipQuery,
  readOneSectionRoleForApplication,
  readOneSectionRoleQuery,
  readOneSectionSlotForCheckinQuery,
  readOneUserQuery,
  readSectionSlotsByRoleQuery
} from '../../_lib/graphql/queries'
import { useServiceWorkerRegistration } from '../PWAUpdate/PWAUpdatePrompt'

type PropTypes = {
  children: ReactNode
}

interface WebSocketContextType {
  hasSocketConnection: boolean
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocket = () => useContext(WebSocketContext)

export const Websocket = ({children}: PropTypes) => {
  const initRef = useRef(false)
  const [hasSocketConnection, setHasSocketConnection] = useState(false)
  const client = useClient()
  const getServiceWorker = useServiceWorkerRegistration()

  const handleMessage = useCallback((message: any) => {
    let vars: any, queries = null
    let hasData: any
    switch (message.data.object) {
      case 'SW':
        console.log('SERVICE WORKER UPDATE', message)
        if ('serviceWorker' in navigator) {
          const swRegistration = getServiceWorker()
          if (swRegistration) {
            swRegistration.update().then(r => console.log(r));
          }

          navigator.serviceWorker.ready
            .then((registration) => {
              registration.update().then(r => console.log(r));
            })
            .catch((error) => {
              console.error(error.message);
            });
        }
        break;
      case 'UserMembership':
        vars = { input: { membershipID: message.data.value } }
        queries = [readOneMembershipQuery]
        hasData = (value: any) => {
          const data = value?.readOneMembership || {}
          return Object.keys(data).findIndex(key => key !== 'id' && data[key] !== null) >= 0
        }
        break;
      case 'UserGroup':
        vars = { input: { groupID: message.data.value } }
        queries = [readOneGroupQuery]
        hasData = (value: any) => {
          const data = value?.readOneGroup || {}
          return Object.keys(data).findIndex(key => key !== 'id' && data[key] !== null) >= 0
        }
        break;
      case 'User':
        vars = { input: { userID: message.data.value } }
        queries = [readOneUserQuery]
        hasData = (value: any) => {
          const data = value?.readOneUser || {}
          return Object.keys(data).findIndex(key => key !== 'id' && data[key] !== null) >= 0
        }
        break;
      case 'SectionRole':
        vars = { roleID: message.data.value}
        queries = [readOneSectionRoleQuery, readOneSectionRoleForApplication]
        hasData = (value: any) => {
          const data = value?.readOneSectionRole || {}
          return Object.keys(data).findIndex(key => key !== 'id' && data[key] !== null) >= 0
        }
        break;
      case 'SectionSlotsByRole':
        vars = { input: { date: message.data.value?.date, roleID: message.data.value?.roleID } }
        queries = [readSectionSlotsByRoleQuery]
        hasData = (value: any) => {
          const data = value?.readOneSectionRole || {}
          return Object.keys(data).findIndex(key => key !== 'id' && data[key] !== null) >= 0
        }
        break;
      case 'SectionSlot':
        vars = { ID: message.data.value }
        queries = [readOneSectionSlotForCheckinQuery]
        hasData = (value: any) => true
        break;
      case 'CateringMealDistribution':
        vars = {}
        queries = [readCateringVouchersQuery]
        hasData = (value: any) => {
          console.log('START CHECKING CATERING VOUCHER', value)
          const data = value?.readMyself || {}
          console.log('CATERING VOUCHER DATA', data)
          return Object.keys(data).findIndex(key => key !== 'id' && data[key] !== null) >= 0
            && data.id === message.data.value?.userID
        }
        break;
      default:
    }

    if (queries && queries.length > 0) {
      queries.forEach(query => {

        const queryData = client.readQuery(query, vars as any)?.data

        // console.log(queryData)
        if (hasData && hasData(queryData)) {
          client.query(query, vars as any).toPromise()
        } else {
          console.log('Ignored because of emtpy query', message)
        }
      })
    }
  }, [client])

  const connect = useCallback(() => {
    console.log('Try to connect')
    const ws = new WebSocket('wss://ws.beckenrocker.de/');
    // const ws = new WebSocket('ws://node.rockambeckenrand.docker:8888/');

    ws.addEventListener('open', () => {
      console.log('connected to ws');

      const token = sessionStorage.getItem('socket-id')

      if (!token) {
        ws.send(JSON.stringify({
          'type': 'register'
        }))
      } else {
        ws.send(JSON.stringify({
          'type': 'login',
          'clientID': token
        }))
      }

      setHasSocketConnection(true)
    });

    ws.addEventListener('close', () => {
      console.log('disconnected');
      setHasSocketConnection(false)
      setTimeout(() => connect(), 1000)
    });

    ws.addEventListener("message", event => {
      if (event.data instanceof Blob) {
        const reader = new FileReader();

        reader.onload = () => {
          if (typeof reader.result !== "string") {
            return
          }

          const result = JSON.parse(reader.result)

          let stage = 'live'
          if (window.location.host.includes('staging')) {
            stage = 'test'
          } else if (window.location.host.includes('docker:')) {
            stage = 'dev'
          }

          console.log(window.location.host, 'host')

          if (result?.Stage === stage) {
            console.log('Recived:', result)
            console.log('Own socket id', sessionStorage.getItem('socket-id'))
            if (result.senderID !== sessionStorage.getItem('socket-id')) {
              handleMessage(result)
            } else {
              console.log('Ignore own message')
            }
          } else {
            console.log('Stage ' + stage + ' ignored')
          }
        };

        reader.readAsText(event.data);
      } else {
        const result = JSON.parse(event.data)

        if (result.socketID) {
          sessionStorage.setItem('socket-id', result.socketID)
        }
      }
    });

    ws.addEventListener('error', error => {
      ws.close();
      // setTimeout(() => connect(), 1000)
    })
  }, [handleMessage])

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true
      connect()
    }
  }, [connect])

  return (
    <WebSocketContext.Provider value={{hasSocketConnection}}>
      {children}
    </WebSocketContext.Provider>
  )
}
